import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, CheckCheck, ChevronDown, Inbox, RefreshCw, X } from 'lucide-react';
import type { ProductFlagInboxRow } from '@sourcito/schemas';
import { Badge, Button, Card } from '../design';
import { useBulkDismissFlags, useProductFlags, useReviewProductFlag } from '../lib/productFlags';

/**
 * Phase 1.9 Pass 4 — /inbox triage surface.
 *
 * Rule-based-flagged products waiting on operator triage. Bulk select +
 * dismiss mirrors the pricing-sheet review UX. Promote → status='interested'
 * fires the existing supplier-discovery auto-trigger (no AI in the decision
 * path; the operator is always the one clicking).
 */
export function InboxPage() {
  const [decisionFilter, setDecisionFilter] = useState<
    'pending' | 'promoted' | 'dismissed' | 'all'
  >('pending');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<'score' | 'flaggedAt' | 'roi'>('score');

  const flags = useProductFlags({
    decision: decisionFilter === 'all' ? undefined : decisionFilter,
    limit: 100,
  });
  const review = useReviewProductFlag();
  const bulkDismiss = useBulkDismissFlags();

  const items = useMemo(() => {
    const list = flags.data?.items ?? [];
    return [...list].sort((a, b) => {
      if (sortBy === 'flaggedAt') {
        return new Date(b.flaggedAt).getTime() - new Date(a.flaggedAt).getTime();
      }
      if (sortBy === 'roi') {
        const ra = a.product.estimatedRoiPct ?? -Infinity;
        const rb = b.product.estimatedRoiPct ?? -Infinity;
        return rb - ra;
      }
      return b.score - a.score;
    });
  }, [flags.data, sortBy]);

  const visibleIds = items.map((i) => i.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someSelected = visibleIds.some((id) => selectedIds.has(id));

  function toggleSelectAll() {
    if (allSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        visibleIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        visibleIds.forEach((id) => next.add(id));
        return next;
      });
    }
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handlePromote(id: string) {
    try {
      await review.mutateAsync({ id, decision: 'promoted' });
    } catch {
      // Mutation surfaces errors via review.isError below; nothing to do here.
    }
  }

  async function handleDismiss(id: string) {
    try {
      await review.mutateAsync({ id, decision: 'dismissed' });
    } catch {
      // ditto
    }
  }

  async function handleBulkDismiss() {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      await bulkDismiss.mutateAsync({ ids });
      setSelectedIds(new Set());
    } catch {
      // ditto
    }
  }

  return (
    <div className="min-h-screen bg-canvas text-fg-primary">
      <header className="border-b border-border-subtle">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link
              to="/search"
              className="inline-flex items-center gap-1.5 text-sm text-fg-secondary hover:text-fg-primary"
            >
              <ArrowLeft className="size-4" />
              Search
            </Link>
            <span className="text-fg-tertiary">·</span>
            <div className="flex items-center gap-2">
              <Inbox className="size-5 text-accent-fg" />
              <h1 className="text-xl font-medium leading-tight">Inbox</h1>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            leadingIcon={<RefreshCw className="size-4" />}
            onClick={() => flags.refetch()}
            loading={flags.isFetching}
          >
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-8 py-8 space-y-5">
        <Card>
          <div className="space-y-2">
            <p className="text-sm text-fg-secondary">
              Products auto-flagged as <strong>indie candidates</strong> by the rule-based
              classifier (low review count + recent listing). The classifier never decides — it just
              surfaces candidates. <strong>You</strong> promote or dismiss.
            </p>
            <p className="text-xs text-fg-tertiary">
              "Mark interested" flips the product's status and fires the existing supplier-discovery
              auto-trigger. "Dismiss" clears the flag from your inbox without changing the product.
            </p>
          </div>
        </Card>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs uppercase tracking-wider text-fg-tertiary">Decision:</span>
            {(['pending', 'promoted', 'dismissed', 'all'] as const).map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => {
                  setDecisionFilter(d);
                  setSelectedIds(new Set());
                }}
                className={`px-2.5 py-1 text-xs font-medium rounded-xs transition-colors duration-fast ${
                  decisionFilter === d
                    ? 'bg-accent/[0.18] text-accent-fg'
                    : 'text-fg-secondary hover:text-fg-primary border border-border-subtle'
                }`}
              >
                {d}
              </button>
            ))}
            <span className="ml-3 text-xs uppercase tracking-wider text-fg-tertiary">Sort:</span>
            {(['score', 'flaggedAt', 'roi'] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSortBy(s)}
                className={`px-2.5 py-1 text-xs font-medium rounded-xs transition-colors duration-fast ${
                  sortBy === s
                    ? 'bg-accent/[0.18] text-accent-fg'
                    : 'text-fg-secondary hover:text-fg-primary border border-border-subtle'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          {someSelected && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-fg-tertiary">{selectedIds.size} selected</span>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleBulkDismiss}
                loading={bulkDismiss.isPending}
                leadingIcon={<CheckCheck className="size-4" />}
              >
                Dismiss selected
              </Button>
            </div>
          )}
        </div>

        {flags.isError && (
          <Card className="border-negative/30 bg-negative/[0.06]">
            <p className="text-sm text-negative">
              Failed to load inbox: {(flags.error as Error)?.message}
            </p>
          </Card>
        )}

        {flags.isLoading && <p className="text-sm text-fg-tertiary">Loading…</p>}

        {flags.data && items.length === 0 && (
          <Card className="border-dashed py-10 text-center space-y-2">
            <Inbox className="size-8 text-fg-tertiary mx-auto" />
            <p className="text-sm font-medium text-fg-primary">
              {decisionFilter === 'pending'
                ? 'No candidates to triage right now.'
                : `No ${decisionFilter} flags yet.`}
            </p>
            <p className="text-xs text-fg-tertiary">
              {decisionFilter === 'pending'
                ? 'When a scheduled query finds a low-review, recently-listed product, it lands here.'
                : 'Triage flags from the Pending tab to populate history.'}
            </p>
          </Card>
        )}

        {items.length > 0 && (
          <div className="border border-border-subtle rounded-md bg-surface overflow-hidden">
            <table className="w-full text-sm tabular-nums">
              <thead className="border-b border-border-subtle bg-surface-elev">
                <tr className="text-xs uppercase tracking-wider text-fg-tertiary">
                  <th className="text-left px-3 py-2.5 font-medium w-8">
                    <input
                      type="checkbox"
                      aria-label="Select all visible"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th className="text-left px-3 py-2.5 font-medium">Product</th>
                  <th className="text-left px-3 py-2.5 font-medium">Signals</th>
                  <th className="text-right px-3 py-2.5 font-medium">Score</th>
                  <th className="text-right px-3 py-2.5 font-medium">Reviews</th>
                  <th className="text-right px-3 py-2.5 font-medium">BSR</th>
                  <th className="text-right px-3 py-2.5 font-medium">ROI</th>
                  <th className="text-left px-3 py-2.5 font-medium">Status</th>
                  <th className="text-right px-3 py-2.5 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((flag) => (
                  <InboxRow
                    key={flag.id}
                    flag={flag}
                    selected={selectedIds.has(flag.id)}
                    onToggleSelect={() => toggleSelect(flag.id)}
                    onPromote={() => handlePromote(flag.id)}
                    onDismiss={() => handleDismiss(flag.id)}
                    pending={review.isPending}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

function InboxRow({
  flag,
  selected,
  onToggleSelect,
  onPromote,
  onDismiss,
  pending,
}: {
  flag: ProductFlagInboxRow;
  selected: boolean;
  onToggleSelect: () => void;
  onPromote: () => void;
  onDismiss: () => void;
  pending: boolean;
}) {
  const isPending = flag.reviewedDecision === 'pending';
  return (
    <tr className="border-t border-border-subtle hover:bg-surface-elev">
      <td className="px-3 py-2.5">
        <input
          type="checkbox"
          aria-label={`Select flag ${flag.id}`}
          checked={selected}
          onChange={onToggleSelect}
          disabled={!isPending}
        />
      </td>
      <td className="px-3 py-2.5">
        <Link
          to="/products/$asin"
          params={{ asin: flag.product.asin }}
          className="block hover:underline"
        >
          <div className="font-mono text-xs text-fg-tertiary">{flag.product.asin}</div>
          <div className="text-sm text-fg-primary max-w-md truncate">
            {flag.product.title ?? '—'}
          </div>
          {flag.product.brand && (
            <div className="text-xs text-fg-tertiary">{flag.product.brand}</div>
          )}
        </Link>
      </td>
      <td className="px-3 py-2.5">
        <div className="flex flex-wrap gap-1">
          {flag.signals.map((s) => (
            <Badge key={s} variant="accent" className="text-xs">
              {s.replace(/_/g, ' ')}
            </Badge>
          ))}
        </div>
      </td>
      <td className="px-3 py-2.5 text-right">{(flag.score * 100).toFixed(0)}</td>
      <td className="px-3 py-2.5 text-right">
        {flag.product.reviewCount === null ? '—' : flag.product.reviewCount.toLocaleString()}
      </td>
      <td className="px-3 py-2.5 text-right">
        {flag.product.currentBsr === null ? '—' : flag.product.currentBsr.toLocaleString()}
      </td>
      <td className="px-3 py-2.5 text-right">
        {flag.product.estimatedRoiPct === null
          ? '—'
          : `${(flag.product.estimatedRoiPct * 100).toFixed(0)}%`}
      </td>
      <td className="px-3 py-2.5">
        <Badge
          variant={
            flag.product.status === 'interested'
              ? 'accent'
              : flag.product.status === 'pursuing'
                ? 'positive'
                : flag.product.status === 'passed'
                  ? 'neutral'
                  : 'warning'
          }
          className="text-xs capitalize"
        >
          {flag.product.status}
        </Badge>
      </td>
      <td className="px-3 py-2.5">
        <div className="flex items-center justify-end gap-1">
          {isPending ? (
            <>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={onPromote}
                disabled={pending}
                leadingIcon={<ChevronDown className="size-3.5 rotate-90" />}
                title="Mark product as interested and fire supplier discovery"
              >
                Mark interested
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onDismiss}
                disabled={pending}
                aria-label="Dismiss"
              >
                <X className="size-3.5" />
              </Button>
            </>
          ) : (
            <span className="text-xs text-fg-tertiary capitalize">{flag.reviewedDecision}</span>
          )}
        </div>
      </td>
    </tr>
  );
}
